import { useMemo, useState } from 'react';
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import CloseIcon from '@mui/icons-material/Close';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { containerHints, hintsContainer, remainingQty, unallocatedItems } from '../../../lib/packingListAllocation';
import { isSuccess } from '../../../lib/packingListRequest';
import { gridSx } from 'shared/config/Styles';
import AllocSplitInput from './AllocSplitInput';
import AllocationSourceBadge from './AllocationSourceBadge';

/**
 * 미배분 라인 한 줄. 행을 누르면 선택(여러 줄 가능)되고, 끝의 분할 아이콘을 누르면 컨테이너별 수량 입력이 열린다.
 * PL 라인에 적힌 컨테이너는 House 컨테이너와 맞는지 색으로, PL 하단에만 적힌 번호는 흐리게 힌트로 띄운다.
 */
function UnallocatedRow({ item, remaining, selected, onClick, onSplit, hints, hintMatch, disabled }) {
    return (
        <Box onClick={disabled ? undefined : onClick} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 1, pr: 0.5, py: 0.5, borderBottom: '1px solid #f1f1f1', cursor: disabled ? 'default' : 'pointer', bgcolor: selected ? 'rgba(0,100,126,0.12)' : 'transparent', borderLeft: selected ? '3px solid #00647e' : '3px solid transparent', '&:hover': { bgcolor: selected ? 'rgba(0,100,126,0.16)' : '#f8f8f8' } }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 11, fontWeight: selected ? 600 : 500 }} noWrap>{item.line_no}. {item.description}</Typography>
                <Typography sx={{ fontSize: 10, color: '#6b7a82' }}>
                    Unallocated {remaining} / {item.qty} {item.qty_uom}
                    {hints.fromLine && <span style={{ color: hintMatch ? '#00647e' : '#b26a00', marginLeft: 6 }}>On PL: {hints.numbers[0]}</span>}
                    {!hints.fromLine && hints.numbers.length > 0 && <span style={{ color: '#8a979e', marginLeft: 6 }}>PL lists: {hints.numbers.join(', ')}</span>}
                </Typography>
            </Box>
            <Tooltip title="Split quantity across containers" placement="left">
                <span>
                    <IconButton size="small" aria-label="Split quantity across containers" disabled={disabled} onClick={(e) => { e.stopPropagation(); onSplit(); }} sx={{ p: 0.25, color: '#5a6b74', '&:hover': { color: 'primary.main' } }}>
                        <CallSplitIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
}

/**
 * 좌 미배분 라인 / 우 컨테이너 박스. 행을 눌러 여러 줄을 고르고 벌크 버튼으로 전량 배분하면 대부분 끝난다.
 * 분할이 필요한 라인만 행 끝 아이콘으로 컨테이너별 수량 입력을 연다. 미배분이 남아도 저장은 막지 않는다.
 * 버튼 한 번의 변경은 onSave 한 번(품목별 전체 entries)으로 서버에 보내고, 화면은 서버가 받아들인 뒤 다시 읽은 값으로 바뀐다.
 * 저장에는 그 변경을 정할 때 보고 있던 allocation_version을 싣는다. 분할 입력은 창을 연 시점의 버전이라, 그사이 동료가 배분을 바꿨다면
 * 서버가 409로 거절하고 화면은 최신 배분을 다시 읽는다.
 */
export default function ContainerAllocPanel({ hbl, items, allocations, allocationVersion, documentHints, onSave }) {
    const [saving, setSaving] = useState(false);
    const [selectedIds, setSelected] = useState([]);
    const [splitItem, setSplitItem] = useState(null);
    const [splitValues, setSplitValues] = useState({});
    const [splitBaseVersion, setSplitBaseVersion] = useState(null);
    const containers = hbl.containers;
    const pending = useMemo(() => unallocatedItems(items, allocations), [items, allocations]);
    // 재조회로 다 실린 라인은 선택에서 빠진다.
    const selected = useMemo(() => selectedIds.filter((id) => pending.some((it) => it.id === id)), [selectedIds, pending]);
    const busy = saving;
    const hintsOf = (item) => containerHints(item, documentHints);

    if (containers.length === 0) {
        return <Typography sx={{ fontSize: 12, color: '#6b7a82' }}>No containers on this HBL yet. Allocation becomes available once containers are registered.</Typography>;
    }
    if (containers.length === 1) {
        return (
            <Chip size="small" label={`Single container · all lines auto-allocated to ${containers[0].cntr_number}`} sx={{ bgcolor: '#2e7d32', color: 'white', fontSize: 11 }} />
        );
    }

    /**
     * 품목 id → 새 entries 묶음을 한 번에 저장한다. 거절되면 화면은 그대로(서버 값) 남는다.
     * 충돌(409)이면 고른 줄과 분할 입력이 옛 배분 기준이라 비운다.
     */
    const save = async (changes, baseVersion = allocationVersion) => {
        if (!Object.keys(changes).length || !onSave) return false;
        setSaving(true);
        const result = await onSave(changes, baseVersion);
        setSaving(false);
        if (result?.status === 409) {
            setSelected([]);
            setSplitItem(null);
        }
        return Boolean(result && isSuccess(result));
    };

    /** 이 컨테이너 몫에 qty를 더한 품목의 새 entries. */
    const withAdded = (item, containerId, qty) => {
        const current = (allocations[item.id] ?? []).find((a) => a.container_id === containerId);
        const others = (allocations[item.id] ?? []).filter((a) => a.container_id !== containerId);
        return [...others, { container_id: containerId, alloc_qty: Number(current?.alloc_qty ?? 0) + qty }];
    };

    const sendTo = async (targets, containerId) => {
        const changes = Object.fromEntries(targets.map((it) => [it.id, withAdded(it, containerId, remainingQty(it, allocations))]));
        if (await save(changes)) {
            setSelected([]);
            setSplitItem(null);
        }
    };
    const sendSelectedTo = (containerId) => sendTo(pending.filter((it) => selected.includes(it.id)), containerId);
    const sendRestTo = (containerId) => sendTo(pending, containerId);

    /** 한 라인의 이 컨테이너 몫만 미배분으로 돌려보낸다. 다른 컨테이너 몫은 그대로다. */
    const returnLine = (item, containerId) => save({ [item.id]: (allocations[item.id] ?? []).filter((a) => a.container_id !== containerId) });

    /** 컨테이너에 실린 라인을 전부 미배분으로 돌려보낸다. 적재 계획을 다시 짤 때 쓴다. */
    const clearContainer = (container) => {
        const loaded = items.filter((it) => (allocations[it.id] ?? []).some((a) => a.container_id === container.id));
        return save(Object.fromEntries(loaded.map((it) => [it.id, allocations[it.id].filter((a) => a.container_id !== container.id)])));
    };

    const openSplit = (item) => {
        const values = {};
        (allocations[item.id] ?? []).forEach((a) => (values[a.container_id] = a.alloc_qty));
        setSplitValues(values);
        setSplitBaseVersion(allocationVersion);
        setSplitItem(item);
    };

    /** 행 클릭 = 선택 토글. 분할 입력은 행 끝 아이콘으로만 연다. */
    const toggleRow = (item) => setSelected((s) => (s.includes(item.id) ? s.filter((x) => x !== item.id) : [...s, item.id]));
    const saveSplit = async () => {
        if (await save({ [splitItem.id]: containers.map((c) => ({ container_id: c.id, alloc_qty: Number(splitValues[c.id]) || 0 })) }, splitBaseVersion)) setSplitItem(null);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, minHeight: 260, minWidth: 0 }}>
                <Box sx={{ width: '25%', border: '1px solid #e6ebee', borderRadius: '8px', bgcolor: 'white', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.5, bgcolor: '#f1f1f1' }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#01354d' }}>Unallocated Lines ({pending.length}){selected.length ? ` · ${selected.length} selected` : ''}</Typography>
                        {pending.length > 0 && (
                            <Button size="small" disabled={busy} onClick={() => { if (selected.length === pending.length) { setSelected([]); setSplitItem(null); } else setSelected(pending.map((p) => p.id)); }} sx={{ fontSize: 11, py: 0, minWidth: 0 }}>
                                {selected.length === pending.length ? 'Clear' : 'Select all'}
                            </Button>
                        )}
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {pending.length === 0 && <Typography sx={{ fontSize: 11, color: '#6b7a82', p: 1 }}>No unallocated lines.</Typography>}
                        {pending.map((it) => (
                            <UnallocatedRow key={it.id} item={it} remaining={remainingQty(it, allocations)} selected={selected.includes(it.id)} onClick={() => toggleRow(it)} onSplit={() => openSplit(it)} hints={hintsOf(it)} hintMatch={containers.some((c) => hintsContainer(hintsOf(it), c))} disabled={busy} />
                        ))}
                    </Box>
                </Box>
                {/* minmax(0, 1fr)·minWidth 0: 1fr의 기본 최소폭(auto)이 그리드 내용 폭을 따라가면 DataGrid가 부모 폭을 다시 재며 끝없이 넓어진다. */}
                <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: `repeat(${Math.min(containers.length, 3)}, minmax(0, 1fr))`, gap: 1 }}>
                    {containers.map((c) => {
                        const hinted = pending.filter((it) => selected.includes(it.id) && hintsContainer(hintsOf(it), c)).length;
                        const lines = items.filter((it) => (allocations[it.id] ?? []).some((a) => a.container_id === c.id));
                        return (
                            <Box key={c.id} data-testid="pl-container-box" sx={{ minWidth: 0, border: '1px solid #e6ebee', borderRadius: '8px', bgcolor: 'white', p: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#01354d' }}>{c.cntr_number} <span style={{ fontWeight: 400, color: '#6b7a82' }}>{c.volume}</span></Typography>
                                    {lines.length > 0 && (
                                        <Button size="small" disabled={busy} onClick={() => clearContainer(c)} title="Return every line in this container to unallocated" sx={{ fontSize: 11, py: 0, minWidth: 0 }}>Clear</Button>
                                    )}
                                </Box>
                                <Button size="small" variant="contained" disabled={!selected.length || busy} onClick={() => sendSelectedTo(c.id)} sx={{ fontSize: 11, bgcolor: '#00647e' }}>
                                    Selected lines → this container{hinted ? ` (${hinted} on PL)` : ''}
                                </Button>
                                <Button size="small" variant="outlined" disabled={!pending.length || busy} onClick={() => sendRestTo(c.id)} sx={{ fontSize: 11 }}>
                                    All remaining → this container
                                </Button>
                                {/* autoHeight: 그리드 높이를 행 수로 정해 상세 패널(높이 auto)과의 크기 측정 순환을 끊는다. 넘치면 래퍼가 스크롤한다. */}
                                <Box sx={{ maxHeight: 190, minWidth: 0, width: '100%', border: '1px solid #e6ebee', borderRadius: 1, overflow: 'auto' }}>
                                    <DataGridPremium
                                        rows={lines.map((it) => {
                                            const a = allocations[it.id].find((x) => x.container_id === c.id);
                                            return { id: it.id, line_no: it.line_no, description: it.description, alloc_qty: a.alloc_qty, qty_uom: it.qty_uom, source: a.allocation_source, item: it };
                                        })}
                                        columns={[
                                            { field: 'line_no', headerName: '#', width: 44, type: 'number', headerAlign: 'center', align: 'center' },
                                            { field: 'description', headerName: 'Description', flex: 1, minWidth: 120, cellClassName: 'pl-desc-cell' },
                                            { field: 'alloc_qty', headerName: 'Qty', width: 70, type: 'number', headerAlign: 'center', valueFormatter: (v, row) => `${v} ${row.qty_uom ?? ''}` },
                                            { field: 'source', headerName: 'Source', width: 84, headerAlign: 'center', align: 'center', display: 'flex', renderCell: (p) => <AllocationSourceBadge source={p.value} /> },
                                            {
                                                field: '__return',
                                                headerName: '',
                                                width: 36,
                                                sortable: false,
                                                align: 'center',
                                                display: 'flex',
                                                renderCell: (p) => (
                                                    <IconButton size="small" disabled={busy} onClick={(e) => { e.stopPropagation(); returnLine(p.row.item, c.id); }} sx={{ p: 0, color: '#5a6b74', '&:hover': { color: 'error.main' } }}>
                                                        <CloseIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                ),
                                            },
                                        ]}
                                        autoHeight
                                        disableVirtualization
                                        density="compact"
                                        hideFooter
                                        disableColumnMenu
                                        disableColumnSorting
                                        disableRowSelectionOnClick
                                        onRowClick={(p) => !busy && openSplit(p.row.item)}
                                        rowHeight={29}
                                        columnHeaderHeight={28}
                                        localeText={{ noRowsLabel: 'No lines in this container' }}
                                        sx={{ ...gridSx, '& .MuiDataGrid-row': { cursor: busy ? 'default' : 'pointer' }, '& .pl-desc-cell': { pl: 1 } }}
                                    />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
            <AllocSplitInput open={Boolean(splitItem)} item={splitItem} containers={containers} hints={splitItem ? hintsOf(splitItem) : { numbers: [], fromLine: false }} values={splitValues} saving={saving} onChange={(cid, v) => setSplitValues((s) => ({ ...s, [cid]: v }))} onSave={saveSplit} onCancel={() => setSplitItem(null)} />
        </Box>
    );
}
